import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../auth/auth.context';
import { getUserRole, getUserProfile } from '../services/userService';
import customshineIcon from '../public/icon/customshine.png';
import ColorModeSelect from '../page/shared-theme/ColorModeSelect';
import MyFilesDialog from './MyFilesDialog';
import UserRoleDialog from './UserRoleDialog';


const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  boxShadow: theme.palette.mode === 'dark'
    ? 'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px'
    : 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
}));

const Header = ({ showHomeButton = true }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, userProfile } = useAuth(); // Lấy userProfile từ context
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [userRoleDialogOpen, setUserRoleDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Use profile data from context
  const userRole = userProfile?.role || 'installer';
  const userDisplayName = userProfile?.displayName || 'User';

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleMyFilesClick = () => {
    setFilesDialogOpen(true);
  };

  const handleUserRoleClick = () => {
    setUserRoleDialogOpen(true);
  };

  const handleRoleUpdated = (newRole) => {
    // Không cần update global cache nữa
    // Nếu muốn update UI ngay lập tức, có thể cần refresh lại page hoặc update context
    // Tuy nhiên, context thường sẽ được sync nếu initializeUser được gọi lại
    // Ở đây ta cứ để UI tự update khi user reload hoặc navigate

    // Tạm thời nếu thay đổi role thủ công (dev mode?), ta có thể force update window location
    // hoặc notify user
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileMenuItemClick = (action) => {
    handleMobileMenuClose();
    action();
  };

  return (
    <StyledAppBar position="fixed">
      <Toolbar sx={{ pr: '24px', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img src={customshineIcon} alt="Customshine" style={{ height: 36, width: 36 }} />
          <Box sx={{ height: 28, color: 'text.primary', display: 'flex', alignItems: 'center' }}>
            {/* <svg
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 325 49"
              xmlSpace="preserve"
              style={{ height: '90%', width: 'auto', fill: 'currentColor' }}
            >
              <g>
                <path
                  d="M21.998,0.728L0,48.256h4.711L23.887,6.235l19.121,42.021h4.904L25.943,0.728H21.998z M51.19,48.256h4.469V0.728H51.19V48.256z M288.098,0.728l1.944,4.191h14.272v43.309h4.469V4.919h14.272L325,0.728 C325,0.728,288.098,0.728,288.098,0.728z M127.107,22.261L127.107,22.261c-8.52-1.755-13.147-3.809-13.147-9.671v-0.128 c0-4.794,4.575-8.274,10.847-8.274c5.009-0.092,9.862,1.742,13.559,5.123l0.384,0.355l2.844-3.535l-0.384-0.329 c-4.529-3.855-10.324-5.897-16.269-5.733c-8.82,0-15.451,5.485-15.451,12.739v0.136c0,7.341,4.656,11.286,16.08,13.588 c8.274,1.672,12.739,3.644,12.739,9.395v0.136c0,5.15-4.629,8.735-11.258,8.735c-6.15,0.136-12.075-2.32-16.326-6.766l-0.384-0.355 l-2.931,3.37l0.384,0.329c5.112,4.932,11.963,7.649,19.066,7.561c9.369,0,15.916-5.424,15.916-13.204v-0.134 c-0.002-7.089-4.686-11.093-15.67-13.34V22.261z M191.619,0.728L173.21,27.847L154.8,0.728h-4.026v47.534h4.329V8.974 l18.024,26.239l18.024-26.239v39.283h4.469V0.728H191.619z M220.929,0.728l-21.998,47.528h4.711l19.198-41.994l19.121,41.994h4.904 L224.9,0.728H220.929z M283.901,48.262l-17.969-23.338h1.562c2.455,0.065,4.909-0.156,7.313-0.658 c5.013-1.343,8.026-4.766,8.026-9.505v-0.173c0-6.053-4.794-9.669-12.848-9.669h-15.367v43.309h-4.469V0.728H270.2 c4.933-0.255,9.766,1.449,13.45,4.739c2.356,2.397,3.674,5.624,3.671,8.985v0.136c0,7.205-4.904,12.245-13.204,13.669 l15.477,19.997L283.901,48.262z M99.133,48.262L81.167,24.916h1.562c2.455,0.065,4.909-0.156,7.313-0.658 c5.013-1.343,8.026-4.766,8.026-9.505v-0.165c0-6.053-4.794-9.669-12.848-9.669H69.853v43.309h-4.469V0.728h20.052 c4.933-0.255,9.766,1.449,13.45,4.739c2.358,2.396,3.679,5.623,3.677,8.985v0.136c0,7.205-4.904,12.245-13.204,13.669 l15.477,19.997L99.133,48.262z"
                  fill="currentColor"
                />
              </g>
            </svg> */}

          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Desktop Menu */}
          {!isMobile && (
            <>
              {showHomeButton && (
                <IconButton color="inherit" onClick={handleHomeClick} aria-label="home">
                  <HomeIcon />
                </IconButton>
              )}
              <Button
                color="inherit"
                startIcon={<PersonIcon />}
                onClick={handleUserRoleClick}
                sx={{ textTransform: 'none' }}
              >
                {userDisplayName}
              </Button>
              <Button
                color="inherit"
                startIcon={<FolderIcon />}
                onClick={handleMyFilesClick}
                sx={{ textTransform: 'none' }}
              >
                My Files
              </Button>
              <IconButton color="inherit" onClick={handleLogout} aria-label="logout">
                <LogoutIcon />
              </IconButton>
              <ColorModeSelect sx={{ ml: 2 }} />
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <>
              {showHomeButton && (
                <IconButton color="inherit" onClick={handleHomeClick} aria-label="home">
                  <HomeIcon />
                </IconButton>
              )}
              <IconButton
                color="inherit"
                onClick={handleMobileMenuToggle}
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>

      {/* My Files Dialog */}
      <MyFilesDialog
        open={filesDialogOpen}
        onClose={() => setFilesDialogOpen(false)}
        userRole={userRole}
      />

      {/* User Role Dialog */}
      <UserRoleDialog
        open={userRoleDialogOpen}
        onClose={() => setUserRoleDialogOpen(false)}
        onRoleUpdated={handleRoleUpdated}
      />

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          zIndex: 1400,
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'background.default',
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Menu
          </Typography>
          <IconButton onClick={handleMobileMenuClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ pt: 0 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileMenuItemClick(handleUserRoleClick)}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText
                primary={userDisplayName}
                secondary={userRole.charAt(0).toUpperCase() + userRole.slice(1)}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileMenuItemClick(handleMyFilesClick)}>
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText primary="My Files" />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 1 }} />
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <ColorModeSelect sx={{ minWidth: 'auto', ml: 0.5 }} />
              </ListItemIcon>
              <ListItemText primary="Theme" />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 1 }} />
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileMenuItemClick(handleLogout)}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </StyledAppBar>
  );
};

export default Header;
